/**
 * Validação de CNPJ conforme regra vigente da Receita Federal (numérico e alfanumérico).
 * 12 primeiras posições: [0-9A-Z]; 2 dígitos verificadores numéricos.
 * Valor no módulo 11 = código ASCII − 48.
 */

const WEIGHTS_DV1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] as const;
const WEIGHTS_DV2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] as const;
const NORMALIZED_PATTERN = /^[0-9A-Z]{12}[0-9]{2}$/;

export function normalizeCnpj(value: string | null | undefined): string {
  if (!value) {
    return '';
  }

  return value
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '');
}

function isRepeatedSequence(value: string): boolean {
  return value.split('').every((character) => character === value[0]);
}

function calculateCheckDigit(source: string, weights: readonly number[]): number {
  let sum = 0;
  for (let index = 0; index < source.length; index += 1) {
    sum += (source.charCodeAt(index) - 48) * weights[index];
  }

  const remainder = sum % 11;
  return remainder < 2 ? 0 : 11 - remainder;
}

export function isValidCnpj(value: string | null | undefined): boolean {
  const normalized = normalizeCnpj(value);
  if (normalized.length !== 14 || !NORMALIZED_PATTERN.test(normalized)) {
    return false;
  }

  if (isRepeatedSequence(normalized)) {
    return false;
  }

  const body = normalized.slice(0, 12);
  const firstCheckDigit = calculateCheckDigit(body, WEIGHTS_DV1);
  const secondCheckDigit = calculateCheckDigit(`${body}${firstCheckDigit}`, WEIGHTS_DV2);

  return (
    normalized[12] === String(firstCheckDigit)
    && normalized[13] === String(secondCheckDigit)
  );
}

/** Máscara 00.000.000/0000-00 (12 primeiras posições alfanuméricas; DV numérico). */
export function formatCnpjMask(value: string | null | undefined): string {
  const raw = (value ?? '').toUpperCase().replace(/[^0-9A-Z]/g, '');
  let body = '';
  let checkDigits = '';

  for (const character of raw) {
    if (body.length < 12) {
      body += character;
      continue;
    }

    if (checkDigits.length < 2 && character >= '0' && character <= '9') {
      checkDigits += character;
    }
  }

  const chars = `${body}${checkDigits}`;
  if (chars.length <= 2) {
    return chars;
  }
  if (chars.length <= 5) {
    return `${chars.slice(0, 2)}.${chars.slice(2)}`;
  }
  if (chars.length <= 8) {
    return `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(5)}`;
  }
  if (chars.length <= 12) {
    return `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(5, 8)}/${chars.slice(8)}`;
  }

  return `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(5, 8)}/${chars.slice(8, 12)}-${chars.slice(12)}`;
}
